export function getRequiredString(value: string | undefined | null, message?: string): string {
    if (!!value) {
        return value
    } else {
        throw new Error(message || 'String undefined')
    }
}

export function getRequiredNumber(value: string | number | undefined, message?: string): number {
    if (typeof value === 'number') {
        return value
    } else if (typeof value === 'string') {
        return parseFloat(value)
    } else {
        throw new Error(message || 'number undefined')
    }
}

export function getOptionalBoolean(value: unknown): boolean | undefined {
    if (value === undefined || value === null) {
        return undefined
    } else if (typeof value === 'string') {
        if (value === '') {
            return undefined
        }
        return value.toLowerCase() === 'true'
    } else if (typeof value === 'boolean') {
        return value as boolean
    } else {
        return !!value
    }
}

export function getRequiredBoolean(value: unknown, message?: string): boolean {
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true'
    } else if (typeof value === 'boolean') {
        return value as boolean
    } else {
        throw new Error(message || 'Boolean undefined')
    }
}

export function randomAlphaNumeric(): string {
    return Math.random().toString(36).slice(2, 10).toUpperCase()
}

export function randomNumeric(size: number): string {
    return Math.round(Math.random() * Math.pow(10, size))
        .toString(10)
        .padStart(size, '0')
}

export function setCharacterAtIndex(original: string, index: number, character: string): string {
    if (index < 0) {
        return original
    }
    if (index >= original.length) {
        return original.padEnd(index, ' ') + character
    }

    const stringAsArray = original.split('')
    stringAsArray[index] = character
    return stringAsArray.join('')
}

export function containsChar(s: string, char: string): boolean {
    return s.indexOf(char) >= 0
}

export function nullableStringToDateOrNow(value: string | null | undefined): Date {
    return !!value ? new Date(value) : new Date()
}

export function nullableStringToNullableNumber(value: string | null): number | null {
    return !!value ? parseFloat(value) : null
}

export function nullableNumberToNullableString(value: number | null | undefined): string | null {
    return !!value ? numberToString(value) : null
}

export function nullableNumberToUndefinedString(value: number | null): string | undefined {
    return !!value ? numberToString(value) : undefined
}

export function undefinedOrNumberToUndefinedOrString(value: number | undefined): string | undefined {
    return !!value ? numberToString(value) : undefined
}

export function numberToString(value: number): string {
    return '' + value
}

export function toLocalizedMonthAndDay(date: Date) {
    return new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
}

export function toLocalizedMonthDayAndTime(date: Date) {
    return `${toLocalizedMonthAndDay(date)} at ${new Date(date).toLocaleTimeString('en-US', { timeStyle: 'short' })}`
}

export function toLocalizedMonthAndDayAndYear(date: Date) {
    return new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

export function toLocalizedMonthAndDayAndDynamicYear(date: Date) {
    const d = date instanceof Date ? date : new Date(date)
    if (d.getUTCFullYear() !== new Date().getUTCFullYear()) {
        return toLocalizedMonthAndDayAndYear(d)
    } else {
        return toLocalizedMonthAndDay(d)
    }
}

export function toInputDateString(date: Date): string {
    const localizedDate = toLocalizedMonthAndDayAndYear(date)
    const [month, day, year] = localizedDate.split('/')
    return `${year}-${month}-${day}`
}

export function toLocalizedCurrencyAmount(amount: string | null): string {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    return formatter.format(parseFloat(amount || '0'))
}

export function upperSnakeToUpperSentenceCase(source: string) {
    var word = '' + source.charAt(0)
    for (var i = 1; i < source.length; i++) {
        const char = source.charAt(i)
        if (source.charAt(i - 1) === '_') {
            word += char
        } else if (char === '_') {
            word += ' '
        } else {
            word += char.toLowerCase()
        }
    }
    return word
}

export function capitalizeFirstChar(source: string): string {
    if (source.length === 0) {
        return ''
    } else if (source.length === 1) {
        return source.charAt(0).toUpperCase()
    } else {
        return source.charAt(0).toUpperCase() + source.slice(1)
    }
}

export function toNullishDate(date: string | undefined | null): Date | undefined {
    try {
        return !!date ? new Date(date) : undefined
    } catch (e: unknown) {
        return undefined
    }
}

export function allNotNullish(...strings: (string | null | undefined)[]): boolean {
    return strings.every((str) => !!str !== null && str !== undefined)
}

export function toNullableString(val: FormDataEntryValue | null): string | null {
    if (!val) {
        return null
    } else {
        return val.toString()
    }
}

export function cleanUrlString(raw: string | null | undefined): string | undefined {
    return (
        !raw ? undefined
        : raw.startsWith('http') ? raw.replace('http://', 'https://')
        : `https://${raw}`
    )
}

export function isValidUrlString(str: string): boolean {
    return URL.canParse(str) && str.indexOf('.') > 0
}

export function getUrlStringHostName(str: string): string {
    if (isValidUrlString(str)) {
        return new URL(str).hostname
    } else {
        return str
    }
}

export function truncateTo(str: string, length: number, padding?: number): string {
    const raw = str.trim()
    const trunc = raw.length > length ? raw.substring(0, length) + '...' : raw
    if (padding) {
        return ' '.repeat(padding) + trunc + ' '.repeat(padding)
    } else {
        return trunc
    }
}
